<template>
  <div ref="scrollContainer" class="relative" data-testid="modview-wrapper">
    <template v-if="items.length > 0">
      <!-- Mobile: all items in a horizontal scroll -->
      <ul
        class="flex overflow-x-auto snap-x snap-mandatory list-none p-0 m-0 gap-2 h-full md:hidden [&>li]:w-75 [&>li]:shrink-0 [&>li]:snap-start sm:[&>li]:w-100"
      >
        <li
          v-for="(item, itemIndex) in items.slice(0, INITIAL_BATCH_SIZE)"
          :key="`${item.key}__${itemIndex}`"
        >
          <landing-page-modview-grid-mod-item
            :item="item"
            @select-mod="onModSelected"
          />
        </li>
      </ul>

      <!-- Desktop: paginated view -->
      <div class="relative hidden md:block">
        <transition
          mode="out-in"
          enter-active-class="transition-opacity duration-200"
          enter-from-class="opacity-0"
          leave-active-class="transition-opacity duration-200"
          leave-to-class="opacity-0"
        >
          <ul
            :key="currentPage"
            class="grid gap-2 list-none p-0 m-0 grid-rows-3 grid-cols-3 h-full"
          >
            <li
              v-for="(item, itemIndex) in paginatedItems[currentPage]"
              :key="`${item.key}__${itemIndex}`"
            >
              <landing-page-modview-grid-mod-item
                :item="item"
                @select-mod="onModSelected"
              />
            </li>
          </ul>
        </transition>

        <button
          v-if="currentPage > 0"
          class="absolute top-1/2 -translate-y-1/2 -left-4 hidden md:flex items-center justify-center aspect-square rounded-full bg-depth-2 p-2.5 text-rhino shadow-xl transition-colors hover:bg-depth-3 hover:text-white"
          type="button"
          aria-label="Previous page"
          @click="prevPage"
        >
          <fa-icon class="size-4" :icon="faChevronLeft" aria-hidden="true" />
        </button>
        <button
          v-if="currentPage < paginatedItems.length - 1"
          class="absolute top-1/2 -translate-y-1/2 -right-4 hidden md:flex items-center justify-center aspect-square rounded-full bg-depth-2 p-2.5 text-rhino shadow-xl transition-colors hover:bg-depth-3 hover:text-white"
          type="button"
          aria-label="Next page"
          @click="nextPage"
        >
          <fa-icon class="size-4" :icon="faChevronRight" aria-hidden="true" />
        </button>
      </div>
    </template>
    <p v-else class="text-center">{{ t('landingpage/mods/not_found') }}</p>
  </div>
</template>

<script setup lang="ts">
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import type { StrapiMinecraftModpack } from '~/apollo/types/types';
import { INITIAL_BATCH_SIZE, PAGINATION_BATCH_SIZE } from './constants';

const props = withDefaults(
  defineProps<{
    items?: StrapiMinecraftModpack[];
    itemsPerPage?: number;
    lazyLoadingOffset?: number;
    isResetting?: boolean;
  }>(),
  {
    items: () => [],
    itemsPerPage: 4,
    lazyLoadingOffset: 2,
    isResetting: false,
  },
);

const emit = defineEmits<{
  (e: 'invoke-pagination', val: { batchSize: number }): void;
  (e: 'select-mod', val: StrapiMinecraftModpack): void;
}>();

const { t } = useI18n();
const currentPage = ref(0);
const scrollContainer = ref<HTMLElement | null>(null);

const sliceIntoChunks = (arr: StrapiMinecraftModpack[], chunkSize: number) => {
  if (chunkSize === 0) return [];
  const res = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    res.push(arr.slice(i, i + chunkSize));
  }
  return res;
};

const paginatedItems = computed(() =>
  sliceIntoChunks(props.items, props.itemsPerPage),
);

const nextPage = () => {
  if (currentPage.value >= paginatedItems.value.length - 1) return;
  currentPage.value += 1;

  if (
    currentPage.value ===
    paginatedItems.value.length - props.lazyLoadingOffset
  ) {
    emit('invoke-pagination', { batchSize: PAGINATION_BATCH_SIZE });
  }
};

const prevPage = () => {
  if (currentPage.value <= 0) return;
  currentPage.value -= 1;
};

// Reset when isResetting becomes true
watch(
  () => props.isResetting,
  (value) => {
    if (value) {
      currentPage.value = 0;
    }
  },
);

// Mobile: trigger pagination when scroll reaches the end
const { smaller } = useAppBreakpoints();
const isSmallerMd = smaller('md');
const { arrivedState } = useScroll(scrollContainer, {
  offset: { right: import.meta.client ? window.innerWidth * 2 : 0 },
});

watch(
  () => arrivedState.right && isSmallerMd.value,
  (value) => {
    if (value) {
      emit('invoke-pagination', { batchSize: PAGINATION_BATCH_SIZE });
    }
  },
);

const onModSelected = (item: StrapiMinecraftModpack) => {
  emit('select-mod', item);
};

// Exposed for parent reset
const resetPosition = () => {
  currentPage.value = 0;
};

defineExpose({ resetPosition });
</script>
